import { Card, makeStyles, useIsMobile, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { EventsMap, useClientOptions } from '@paintscout/ui-business'
import type { CalendarEvent } from 'paintscout'
import type { View } from 'react-big-calendar'
import { Calendar as BigCalendar, momentLocalizer } from 'react-big-calendar'
import React, { useMemo, useState } from 'react'
import moment from 'moment'
import classNames from 'classnames'
import { removeAuth0Prefix } from '@paintscout/util'
import { getCrews } from '@paintscout/util/builder'
import { getMapTitle, handleCalendarNavigate } from '../Calendar/calendarUtils'
import CalendarShim from '../Calendar/CalendarShim'
import { useProductionCalendar } from './ProductionCalendarContext'
import { ProductionCalendarToolbar } from './toolbar'
import EventsAgendaView from './EventsAgendaView'
import { DayView as DayViewComponent } from './day'
import { WeekColumnHeader, WeekEvent } from './week'
import { MonthColumnHeader, MonthDateHeader, MonthEvent } from './month'
import MobileWeekView from './week/MobileWeekView'
import MobileMonthView from './month/MobileMonthView'
import ProductionEventPopper from './ProductionEventPopper'
import ShowMore from './month/ShowMore'

interface StyleArgs {
  view: View
}

const useStyles = makeStyles<Theme, StyleArgs>((theme) => ({
  sharedStyles: {
    overflow: 'visible',
    '& .rbc-header': {
      backgroundColor: theme.paintscout.palette.brand.Neutral.P98,
      borderBottom: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      borderTop: 'none',
      '&:first-child': {
        borderLeft: 'none'
      },
      '&:last-child': {
        borderRight: 'none'
      }
    },
    '& .rbc-row-bg': {
      right: '0px'
    },
    '& .rbc-day-bg': {
      borderColor: theme.paintscout.palette.brand.Neutral.P90,
      '&:first-child': {
        borderLeft: 'none'
      },
      '&:last-child': {
        borderRight: 'none'
      }
    },
    '& .rbc-event': {
      backgroundColor: 'inherit',
      color: 'inherit'
    }
  },
  agendaView: {
    overflowY: 'visible',
    overflowX: 'hidden'
  },
  weekViewStyles: {
    '& .rbc-time-view': {
      padding: theme.spacing(2),
      border: 'none'
    },
    '& .rbc-time-header': {
      border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      borderRadius: theme.borderRadius.md
    },
    '& .rbc-time-header-content': {
      border: 'none'
    },

    '& .rbc-header + .rbc-today': {
      backgroundColor: theme.paintscout.palette.primary,
      color: 'white'
    },
    '& .rbc-time-header-cell': {
      minHeight: '70px'
    },
    '& .rbc-time-header-gutter': {
      display: 'none'
    },
    '& .rbc-time-content': {
      display: 'none'
    }
  },
  monthViewStyles: {
    height: 'max(calc(100dvh - 300px), 800px)',
    [theme.breakpoints.down('xs')]: {
      height: 'fit-content'
    },
    '& .rbc-month-view': {
      padding: theme.spacing(2),
      border: 'none',
      outline: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
      outlineOffset: theme.spacing(-2),
      borderRadius: '1.25rem'
    },
    '& .rbc-month-header': {
      border: 'none'
    },
    '& .rbc-day-bg.rbc-off-range-bg': {
      background: theme.paintscout.palette.brand.Neutral.P98
    },
    '& .rbc-event-content': {
      borderRadius: theme.borderRadius.md
    }
  },
  map: {
    padding: theme.spacing(2),
    paddingBottom: 0,
    marginBottom: theme.spacing(2),
    overflow: 'hidden'
  },
  mapHeader: {
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: theme.paintscout.palette.brand.Neutral.P98,
    border: `1px solid ${theme.paintscout.palette.brand.Neutral.P90}`,
    borderRadius: theme.borderRadius.md,
    borderBottomLeftRadius: 0,
    borderBottomRightRadius: 0,
    borderBottom: 'none'
  }
}))

export default function ProductionCalendar() {
  const { events, date, setDate, view, setView, tab, loading } = useProductionCalendar()
  const classes = useStyles({ view })
  const localizer = momentLocalizer(moment)
  const mobile = useIsMobile({ xs: true })

  const calendarView = tab === 'map' ? 'map' : view

  const handleSetView = (newView: View | 'map') => {
    if (newView === 'map') {
      return
    }

    setView(newView)
  }

  return (
    <Card
      className={classNames(
        { [classes.sharedStyles]: calendarView !== 'agenda' },
        { [classes.weekViewStyles]: view === 'week' },
        { [classes.monthViewStyles]: view === 'month' },
        { [classes.agendaView]: view === 'agenda' }
      )}
    >
      <BigCalendar<CalendarEvent>
        date={date}
        view={calendarView as View}
        onView={handleSetView}
        events={events}
        localizer={localizer}
        timeslots={1}
        step={60}
        formats={{
          dateFormat: 'D',
          timeGutterFormat: 'ha',
          dayFormat: 'MMMM D, YYYY'
        }}
        scrollToTime={moment().toDate()}
        components={{
          toolbar: ProductionCalendarToolbar,
          month: { header: MonthColumnHeader, dateHeader: MonthDateHeader, event: MonthEvent },
          week: { header: WeekColumnHeader, event: WeekEvent },
          showMore: (props) => (
            <ShowMore
              {...props}
              onClick={(date: Date) => {
                setDate(date)
                handleSetView('day')
              }}
            />
          )
        }}
        dayLayoutAlgorithm={'no-overlap'}
        selectable={false}
        views={
          {
            day: loading ? CalendarShim : DayView,
            week: loading ? CalendarShim : mobile ? MobileWeekView : true,
            month: loading ? CalendarShim : mobile ? MobileMonthView : true,
            agenda: loading ? CalendarShim : AgendaView,
            map: loading ? CalendarShim : MapView
          } as Record<string, boolean | React.ComponentType<any>>
        }
        longPressThreshold={5}
        onSelecting={() => false}
        onDrillDown={(date: Date, view: View) => {
          setDate(date)
          handleSetView(view)
        }}
        tooltipAccessor={null}
      />
    </Card>
  )
}

function EventsMapView(props) {
  const { events, date } = props
  const classes = useStyles({ view: 'day' })
  const { selectedUsers } = useProductionCalendar()
  const { options } = useClientOptions()
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(null)
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | null>(null)
  const crews = getCrews({ options })

  const handleMarkerClick = (event: CalendarEvent, clickEvent: React.MouseEvent) => {
    setSelectedEvent(event)
    setAnchorPosition({
      top: clickEvent.clientY,
      left: clickEvent.clientX
    })
  }

  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      const eventStart = new Date(event.start)
      const eventEnd = new Date(event.end)
      const eventIsInDateRange =
        (eventStart <= date && eventEnd >= date) ||
        eventStart.toDateString() === date.toDateString() ||
        eventEnd.toDateString() === date.toDateString()

      const crew = event.crew ? crews.find((c) => c._id === event.crew) : null
      const projectManagerId = crew?.projectManager?.id
      const userIsSelected = selectedUsers.some(
        (user) => removeAuth0Prefix(projectManagerId) === removeAuth0Prefix(user.user_id)
      )
      return eventIsInDateRange && userIsSelected
    })
  }, [events, date, selectedUsers, crews])

  return (
    <div className={classes.map}>
      <div className={classes.mapHeader}>
        <Typography variant="caption" color="textSecondary">
          Total Jobs: {filteredEvents.length}
        </Typography>
        <i className={'fa-regular fa-location-dot'}></i>
      </div>
      <EventsMap
        {...props}
        events={filteredEvents}
        onMarkerClick={(event, clickEvent) => handleMarkerClick(event, clickEvent)} // Pass the click event
        disableTooltip
      />
      {selectedEvent && anchorPosition && (
        <ProductionEventPopper
          event={selectedEvent}
          anchorEl={
            {
              getBoundingClientRect: () => ({
                top: anchorPosition.top,
                left: anchorPosition.left,
                right: anchorPosition.left,
                bottom: anchorPosition.top,
                width: 0,
                height: 0,
                x: anchorPosition.left,
                y: anchorPosition.top,
                toJSON: () => ({
                  top: anchorPosition.top,
                  left: anchorPosition.left,
                  right: anchorPosition.left,
                  bottom: anchorPosition.top,
                  width: 0,
                  height: 0,
                  x: anchorPosition.left,
                  y: anchorPosition.top
                })
              }),
              clientWidth: 0,
              clientHeight: 0
            } as unknown as HTMLElement
          } // Use a virtual element for positioning
          onClose={() => {
            setSelectedEvent(null)
            setAnchorPosition(null)
          }}
          closeOnEdit
        />
      )}
    </div>
  )
}

const AgendaView = (props) => <EventsAgendaView {...props} />

AgendaView.title = (_date: Date) => ``
AgendaView.navigate = (date: Date, action: string) => {
  let newDate = new Date(date)
  switch (action) {
    case 'PREV':
      newDate = moment(date).subtract(1, 'month').toDate()
      break
    case 'NEXT':
      newDate = moment(date).add(1, 'month').toDate()
      break
    case 'TODAY':
      newDate = new Date()
      break
    default:
      break
  }
  return newDate
}

const MapView = (props) => <EventsMapView {...props} />
MapView.title = (date: Date) => getMapTitle(date, 'day')
MapView.navigate = (date: Date, action: string) => handleCalendarNavigate(date, action, 'day')

const DayView = (props) => <DayViewComponent {...props} />
DayView.title = (date: Date) => moment(date).format('dddd, MMMM D, YYYY')
DayView.navigate = (date: Date, action: string) => handleCalendarNavigate(date, action, 'day')

const ScheduleView = (_props) => <>Schedule View</>
ScheduleView.title = (date: Date) => moment(date).format('MMMM YYYY')
ScheduleView.navigate = (date: Date, action: string) => handleCalendarNavigate(date, action, 'month')
